import { getRawMessage } from 'locale'
import { useCallback } from 'react'
import {
  CONDITION_TYPE,
  FIELD_SEMANTIC_TYPE_DICT,
  LOGIC_ACTIONS,
} from '../../../components/Pages/Dashboard/Logic/constants'
import { FIELD_TYPE, FORM_TYPE, typeUtils } from '../../../types'
import { toInfix, toPrefix } from './convertor/convert'

const actionTypeFieldsFilter = {
  jump: (fieldSlug, argIndex) => (item) =>
    item.slug !== fieldSlug &&
    typeUtils.isVisible(item) &&
    !typeUtils.isVariable(item) &&
    !typeUtils.isSuccessPage(item) &&
    !typeUtils.isAdminOnly(item),
  show: (fieldSlug, argIndex) => (item) =>
    item.slug !== fieldSlug &&
    typeUtils.isVisible(item) &&
    !typeUtils.isVariable(item) &&
    !typeUtils.isSuccessPage(item) &&
    !typeUtils.isAdminOnly(item),
  hide: (fieldSlug, argIndex) => (item) =>
    item.slug !== fieldSlug &&
    typeUtils.isVisible(item) &&
    !typeUtils.isVariable(item) &&
    !typeUtils.isSuccessPage(item) &&
    !typeUtils.isAdminOnly(item),
  jump_to_success_page: (fieldSlug, argIndex) => (item) =>
    item.slug !== fieldSlug && item.type === FIELD_TYPE.SUCCESS_PAGE,
  // calc
  add: (fieldSlug, argIndex) => (item) =>
    typeUtils.canBeUsedInCalculationActions(
      item,
      LOGIC_ACTIONS.ADD.key,
      argIndex
    ),
  subtract: (fieldSlug, argIndex) => (item, argIndex) =>
    typeUtils.canBeUsedInCalculationActions(
      item,
      LOGIC_ACTIONS.SUBTRACT.key,
      argIndex
    ),
  multiply: (fieldSlug, argIndex) => (item) =>
    typeUtils.canBeUsedInCalculationActions(
      item,
      LOGIC_ACTIONS.MULTIPLY.key,
      argIndex
    ),
  divide: (fieldSlug, argIndex) => (item) =>
    typeUtils.canBeUsedInCalculationActions(
      item,
      LOGIC_ACTIONS.DIVIDE.key,
      argIndex
    ),
}

const actionTypeFieldsInsert = {
  jump_to_success_page: () => [
    {
      type: 'success_page',
      slug: 'default_success_page',
      title: getRawMessage('logic.defaultSuccessPage'),
    },
  ],
}

const removeFalsyArgs = ({ logic, ...formValues } = {}) => {
  const cleanedLogic = logic?.map(({ actions, ...fieldLogic } = {}) => {
    return {
      ...fieldLogic,
      actions: actions?.map(({ action, args, when }) => ({
        action,
        args: (args || []).filter(Boolean),
        when: when.map(({ type, operator, operation, args }) => {
          return !!type
            ? { type, operator }
            : {
                operation,
                args: (args || []).filter(Boolean),
              }
        }),
      })),
    }
  })

  return { ...formValues, logic: cleanedLogic }
}

const removeEmptyDefaultRules = ({ logic, ...formValues } = {}) => {
  const cleanedLogic = logic?.map(({ actions, ...fieldLogic } = {}) => {
    return {
      ...fieldLogic,
      actions: actions?.filter(({ action, when } = {}) => {
        return (
          !!action ||
          when?.every(
            (condition) =>
              ![
                CONDITION_TYPE.ALWAYS.key,
                CONDITION_TYPE.OTHERWISE.key,
              ].includes(condition?.operation)
          )
        )
      }),
    }
  })

  return { ...formValues, logic: cleanedLogic }
}

const addEmptyDefaultLogic = ({ actions, ...fieldLogic }) => {
  return {
    ...fieldLogic,
    actions: [
      ...actions,
      {
        action: '',
        args: [],
        when: [
          {
            operation: CONDITION_TYPE.ALWAYS.key,
            args: [],
          },
        ],
      },
    ],
  }
}

const hasDefaultLogic = (fieldLogic) =>
  fieldLogic?.actions?.findIndex(({ when } = {}) =>
    when?.every((condition) =>
      [CONDITION_TYPE.ALWAYS.key, CONDITION_TYPE.OTHERWISE.key].includes(
        condition?.operation
      )
    )
  ) !== -1

const hasCrossFieldLogic = (fieldLogic) => {
  const fieldId = fieldLogic?.identifier

  return fieldLogic?.actions?.some((item) =>
    item?.when?.some(
      ({ args }) =>
        args && args[0] && args[0].type === 'field' && args[0].value !== fieldId
    )
  )
}

const hasMultiConditionLogicRule = (fieldLogic) =>
  fieldLogic?.actions?.some(
    (item) =>
      item?.when?.findIndex((condition) => condition?.type === 'operator') !==
      -1
  )

const injectDefaultFieldLogicIfNotExists = (fieldLogic) => {
  return hasDefaultLogic(fieldLogic)
    ? fieldLogic
    : addEmptyDefaultLogic(fieldLogic)
}

const LOGIC_TYPE = {
  SINGLE_PAGE: 'single_page',
  MULTI_PAGE: 'multi_page',
  MIXED: 'mixed',
}

const getLogicType = (form = {}) => {
  const formLogicTypeMap = {
    [FORM_TYPE.SINGLE_PAGE]: LOGIC_TYPE.SINGLE_PAGE,
    [FORM_TYPE.MULTI_PAGE]: LOGIC_TYPE.MULTI_PAGE,
  }

  return formLogicTypeMap[form.form_type]
}

const LogicTypeLogicActions = {
  [LOGIC_TYPE.SINGLE_PAGE]: [
    LOGIC_ACTIONS.SHOW,
    LOGIC_ACTIONS.HIDE,
    LOGIC_ACTIONS.REDIRECT,
    LOGIC_ACTIONS.GOTO_SUCCESS_PAGE,
    // calc
    LOGIC_ACTIONS.ADD,
    LOGIC_ACTIONS.SUBTRACT,
    LOGIC_ACTIONS.MULTIPLY,
    LOGIC_ACTIONS.DIVIDE,
  ],
  [LOGIC_TYPE.MULTI_PAGE]: [
    LOGIC_ACTIONS.JUMP,
    LOGIC_ACTIONS.SUBMIT,
    LOGIC_ACTIONS.REDIRECT,
    LOGIC_ACTIONS.GOTO_SUCCESS_PAGE,
    // calc
    LOGIC_ACTIONS.ADD,
    LOGIC_ACTIONS.SUBTRACT,
    LOGIC_ACTIONS.MULTIPLY,
    LOGIC_ACTIONS.DIVIDE,
  ],
  [LOGIC_TYPE.MIXED]: [
    LOGIC_ACTIONS.SHOW,
    LOGIC_ACTIONS.HIDE,
    LOGIC_ACTIONS.JUMP,
    LOGIC_ACTIONS.SUBMIT,
    LOGIC_ACTIONS.REDIRECT,
    LOGIC_ACTIONS.GOTO_SUCCESS_PAGE,
    // calc
    LOGIC_ACTIONS.ADD,
    LOGIC_ACTIONS.SUBTRACT,
    LOGIC_ACTIONS.MULTIPLY,
    LOGIC_ACTIONS.DIVIDE,
  ],
}

const getFormTypeLogicActions = (form) => {
  const logicType = getLogicType(form)
  const actions = LogicTypeLogicActions[logicType] || []

  return actions.map((o) => ({
    slug: o.key,
    title: o.displayName,
  }))
}

const useFormLogic = (form) => {
  const getLogicActionOptions = useCallback(() => {
    return getFormTypeLogicActions(form)
  }, [form])

  const getFieldLogicIndex = (type, slug) => {
    const fieldLogicIndex = form.visibleFields.findIndex(
      (field) => field.slug === slug
    )

    return fieldLogicIndex !== -1 ? fieldLogicIndex : undefined
  }

  const getIdentifierToIndexMap = () => {
    return form.visibleFields
      .map((field) => field.slug)
      .reduce((acc, curr, index) => ({ ...acc, [curr]: index }), {})
  }

  const getFieldLogicType = (type, subType = '') => {
    return FIELD_SEMANTIC_TYPE_DICT[[type, subType].filter(Boolean).join('.')]
  }

  const getFieldLogicOperations = (type, subType) => {
    return getFieldLogicType(type, subType)?.conditions || []
  }

  const getOperationsForField = (type, slug) => {
    const field = form.fields_list.find((item) => item.slug === slug)

    return getFieldLogicOperations(field?.type, field?.sub_type)
  }

  const getSecondOperationArgInputType = (slug) => {
    const field = getFormField(slug)

    return getFieldLogicType(field?.type, field?.sub_type)
  }

  const getFormField = (slug) => {
    return form.fields_list.find((item) => item.slug === slug)
  }

  const getFieldActionArgs = (type, slug, action, argIndex) => {
    if (!action) {
      return []
    }

    const filterFn =
      typeof actionTypeFieldsFilter[action] === 'function'
        ? actionTypeFieldsFilter[action]
        : () => (x) => x

    const unshiftFn =
      typeof actionTypeFieldsInsert[action] === 'function'
        ? actionTypeFieldsInsert[action]
        : () => []

    return form.fields_list
      .filter(filterFn(slug, argIndex))
      .concat(unshiftFn(slug))
  }

  const getFieldOperationArgs = (type, slug) => {
    const fieldIndex = getFieldLogicIndex(type, slug)

    return form.visibleFields
      .slice(0, fieldIndex + 1)
      .filter((field) => ![FIELD_TYPE.META].includes(field.type))
      .concat(form.hiddenFields)
      .concat(form.variableFields)
      .concat(form.formulaFields)
  }

  const getInitialFieldLogic = useCallback((formObj, fieldSlug) => {
    const logic = formObj?.logic ? formObj?.logic : []
    const existingFieldLogic = logic?.find(
      (logicItem) => logicItem.identifier === fieldSlug
    ) || {
      type: 'field',
      identifier: fieldSlug,
      actions: [],
    }
    const finalFieldLogic =
      injectDefaultFieldLogicIfNotExists(existingFieldLogic)

    return finalFieldLogic
  }, [])

  const getFieldNewDefaultRuleInitialValues = (field) => {
    const operationArg0Slug = field.type !== FIELD_TYPE.META ? field.slug : ''

    return {
      action: 'jump',
      args: [
        {
          // type: "field",
          // identifier: "",
        },
      ],
      when: [
        {
          operation: '',
          args: [
            {
              type: 'field',
              value: operationArg0Slug,
            },
            // {
            // type: "field",
            // value: "",
            // },
          ],
        },
      ],
    }
  }

  const getFormLogic = useCallback(
    (formObj) => {
      const infixedForm = { ...formObj, logic: toInfix(formObj) }

      return infixedForm.visibleFields.map((field) =>
        getInitialFieldLogic(infixedForm, field.slug)
      )
    },
    [getInitialFieldLogic]
  )

  const getFieldLogic = (fieldSlug) => {
    const formLogic = getFormLogic(form)
    const fieldLogicIndex = getFieldLogicIndex('field', fieldSlug)

    return formLogic[fieldLogicIndex]
  }

  const isFieldLogicAdvanced = (fieldLogic) => {
    return (
      hasCrossFieldLogic(fieldLogic) || hasMultiConditionLogicRule(fieldLogic)
    )
  }

  const prepareToSubmit = (formValues) => {
    return {
      logic: toPrefix(removeFalsyArgs(removeEmptyDefaultRules(formValues))),
    }
  }

  return {
    form,
    formLogic: getFormLogic(form),
    getLogicActionOptions,
    getSecondOperationArgInputType,
    getFieldLogicOperations,
    getFieldLogicType,
    getFieldLogicIndex,
    getFieldLogic,
    isFieldLogicAdvanced,
    getIdentifierToIndexMap,
    getFieldOperationArgs,
    getOperationsForField,
    getFormLogic,
    getFormField,
    getFieldActionArgs,
    getFieldNewDefaultRuleInitialValues,
    prepareToSubmit,
  }
}

export default useFormLogic
